/**
 * Pick 추가·수정 흐름.
 *
 *   카테고리 검색 → (없으면) 요청 → 카테고리 선택 → 내용 입력 → 저장
 *
 * 비로그인 화면과 로그인 화면이 **같은 흐름을 쓴다.** 저장하는 곳만 다르므로
 * `onSave`만 갈아끼운다 — 두 벌로 나누면 한쪽만 고치는 실수가 생긴다.
 */

import { useCallback, useEffect, useState } from 'react';

import type { CategoryNode } from '@lifetimeline/shared';

import { Sheet } from '../../components/Sheet.js';
import { useToast } from '../../components/Toast.js';
import { errorMessageKey } from '../../lib/apiClient.js';
import { useI18n } from '../../i18n/index.js';
import { loadCatalog, type LoadedCatalog } from '../../lib/catalog.js';
import { CategoryRequestForm } from '../category/CategoryRequestForm.js';
import { CategorySearch } from '../category/CategorySearch.js';
import { PickEditor, type PickDraft } from './PickEditor.js';

type Stage =
  | { kind: 'closed' }
  | { kind: 'search' }
  | { kind: 'request'; query: string }
  | { kind: 'edit'; category: CategoryNode };

export interface PickFlowController {
  /** 카테고리 검색부터 시작한다 */
  openSearch: () => void;
  /** 이미 고른 Pick을 수정한다. 카테고리는 바꿀 수 없다 */
  openEdit: (categoryId: string) => void;
  /** 화면에 그릴 시트 */
  element: React.ReactNode;
}

/**
 * 흐름이 알아야 하는 최소한의 Pick.
 *
 * 로그인 목록(`PickResponse`)과 비로그인 목록(`LocalPick`)이 둘 다 이 모양을
 * 만족한다. 사진이 여기 없는 이유: 로그인 쪽은 https 주소지만 비로그인 쪽은
 * 브라우저 안의 Blob이라 주소가 없다. 화면에 띄울 주소는 `resolveImageUrl`이
 * 그때그때 만들어 준다.
 */
export interface FlowPick {
  categoryId: string;
  itemName: string;
  itemDescription?: string | undefined;
}

interface UsePickFlowOptions {
  /** 이미 고른 것들. 중복 선택을 막고, 수정 시 기존 값을 채운다 */
  existingPicks: readonly FlowPick[];
  /**
   * 수정 화면에 띄울 기존 사진 주소. 없으면 null.
   * 로그인: 저장된 https 주소 / 비로그인: IndexedDB Blob의 objectURL
   */
  resolveImageUrl?: ((categoryId: string) => string | null) | undefined;
  /** 사진을 올리거나 브라우저에 담는 일은 호출자가 한다 */
  onSave: (draft: PickDraft) => Promise<void>;
  /** 편집 시트 안의 "지우기". 확인은 호출자가 띄운다 */
  onRequestDelete: (categoryId: string) => void;
}

export function usePickFlow({
  existingPicks,
  resolveImageUrl,
  onSave,
  onRequestDelete,
}: UsePickFlowOptions): PickFlowController {
  const { t, language } = useI18n();
  const toast = useToast();
  const [stage, setStage] = useState<Stage>({ kind: 'closed' });
  const [catalog, setCatalog] = useState<LoadedCatalog | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadCatalog(language)
      .then((loaded) => {
        if (!cancelled) {
          setCatalog(loaded);
        }
      })
      .catch(() => {
        // 카탈로그 실패는 CategorySearch가 자기 자리에서 안내한다
      });
    return () => {
      cancelled = true;
    };
  }, [language]);

  const pickedIds = new Set(existingPicks.map((pick) => pick.categoryId));

  const openSearch = useCallback(() => setStage({ kind: 'search' }), []);

  const openEdit = useCallback(
    (categoryId: string) => {
      const category = catalog?.byId.get(categoryId);
      if (category === undefined) {
        // 카탈로그에 없는 카테고리(지워졌거나 아직 못 받음)는 수정할 수 없다.
        // 최소한 이름만이라도 만들어 편집은 되게 한다 — 사용자가 자기 데이터를
        // 손도 못 대는 상황을 만들지 않는다.
        setStage({
          kind: 'edit',
          category: { id: categoryId, name: categoryId, status: 'active' },
        });
        return;
      }
      setStage({ kind: 'edit', category });
    },
    [catalog],
  );

  const close = useCallback(() => setStage({ kind: 'closed' }), []);

  const existingFor = (categoryId: string): FlowPick | undefined =>
    existingPicks.find((pick) => pick.categoryId === categoryId);

  const element = (
    <>
      <Sheet open={stage.kind === 'search'} onClose={close} title={t('category.searchTitle')}>
        <CategorySearch
          pickedCategoryIds={pickedIds}
          onSelect={(category) => setStage({ kind: 'edit', category })}
          onRequestCategory={(query) => setStage({ kind: 'request', query })}
        />
      </Sheet>

      <Sheet open={stage.kind === 'request'} onClose={close} title={t('categoryRequest.title')}>
        {stage.kind === 'request' ? (
          <CategoryRequestForm initialText={stage.query} onDone={close} />
        ) : null}
      </Sheet>

      <Sheet
        open={stage.kind === 'edit'}
        onClose={close}
        title={stage.kind === 'edit' ? stage.category.name : ''}
      >
        {stage.kind === 'edit' ? (
          <PickEditor
            categoryId={stage.category.id}
            categoryName={stage.category.name}
            existing={existingFor(stage.category.id)}
            existingImageUrl={resolveImageUrl?.(stage.category.id) ?? null}
            submitting={saving}
            submitLabel={t('image.uploading')}
            onDelete={() => {
              const { id } = stage.category;
              close();
              onRequestDelete(id);
            }}
            onSubmit={async (draft) => {
              setSaving(true);
              try {
                await onSave(draft);
                close();
              } catch (cause) {
                /*
                 * 실패하면 **말해 준다.** 예전에는 여기서 조용히 끝나
                 * 시트가 열린 채 아무 일도 안 일어났다 — 사용자는 저장이
                 * 된 줄 알고 나가거나, 저장 버튼을 계속 누른다.
                 *
                 * 시트는 닫지 않는다. 적어 둔 것이 그대로 남아 있어야
                 * 다시 눌러 볼 수 있다.
                 */
                toast.show(t(errorMessageKey(cause)), 'error');
              } finally {
                setSaving(false);
              }
            }}
          />
        ) : null}
      </Sheet>
    </>
  );

  return { openSearch, openEdit, element };
}
